import { DEVICE } from './events.interfaces';
/**
 * Primary player segments based on overall player behavior and status.
 * VIP - High value players with significant activity and deposits
 * Occasional - Players who engage sporadically with moderate activity
 * NewUser - Recently registered players still in onboarding phase
 * Inactive - Players with no recent activity for a defined period
 */
export enum PlayerSegment {
  VIP = 'VIP',
  PREMIUM = 'PREMIUM',
  OCCASIONAL = 'OCCASIONAL',
  NEW_USER = 'NEW_USER',
  INACTIVE = 'INACTIVE',
}

/**
 * Primary product or vertical preference indicating player's main gaming interest.
 * SportBetting - Sports betting and live betting
 * Bicho - Traditional Brazilian lottery-style game
 * Casino - Casino games including slots, table games, live dealer
 */
export enum MainProductPreference {
  SPORTBETTING = 'SPORTBETTING',
  BICHO = 'BICHO',
  CASINO = 'CASINO',
}
/**
 * Enum representing loyalty tier levels from Bronze to Diamond
 */
export enum LoyaltyTier {
  BRONZE = 'Bronze',
  SILVER = 'Silver',
  GOLD = 'Gold',
  PLATINUM = 'Platinum',
  DIAMOND = 'Diamond',
}
/**
 * Comprehensive player profile interface containing segmentation data, preferences,
 * behavioral metrics, and engagement patterns used for personalization and targeting.
 */
export interface IPlayerSegmentation {
  /**
   * Current assigned segment based on holistic analysis of player behavior,
   * including betting patterns, deposit frequency, and engagement levels
   */
  currentSegment: PlayerSegment;

  /**
   * Primary product vertical where player spends most time and money,
   * used for personalized marketing and product recommendations
   */
  mainProductPreference: MainProductPreference;

  /**
   * Activity tracking metrics:
   * lastActiveDate - Timestamp of last player action/session
   * accessFrequencyPerWeek - Average weekly login frequency
   */
  lastActiveDate: number;
  accessFrequencyPerWeek?: number;

  /**
   * Betting volume metrics:
   * totalBetAmount - Cumulative amount wagered across all products
   * averageBetAmount - Mean bet size per transaction
   */
  totalBetAmount?: number;
  averageBetAmount?: number;
  totalBets?: number;
  maxBetAmount?: number;
  minBetAmount?: number;

  /**
   * Product usage distribution showing percentage allocation or
   * absolute amounts wagered across different gaming verticals.
   * Values range 0-100 for percentages or actual currency amounts.
   */
  productPreferences?: {
    sportBetting?: number;
    casino?: number;
    bicho?: number;
  };

  /**
   * Detailed sports betting behavior and preferences including
   * favorite sports, live vs pre-match betting ratio, and notable wins
   */
  sportBettingDetails?: {
    favoriteSports?: string[];
    inPlayVsPreMatch?: {
      inPlayBets?: number;
      preMatchBets?: number;
    };
    biggestWin?: number;
  };

  /**
   * Bicho game specific metrics tracking favorite numbers
   * and engagement frequency with this traditional game format
   */
  bichoDetails?: {
    favoriteNumbers?: number[];
    timesPlayed?: number;
  };

  /**
   * Promotional engagement tracking showing which bonuses and
   * promotional offers the player has utilized
   */
  promotionsUsed?: {
    welcomeBonusUsed?: boolean;
    freeBetsUsed?: number;
    freeSpinsUsed?: number;
  };

  /**
   * RFM (Recency, Frequency, Monetary) scoring metrics used for
   * customer segmentation and value assessment:
   * recencyDays - Days since last activity
   * frequency - Activity rate over time period
   * monetaryValue - Total value generated
   */
  rfm?: {
    recencyDays: number; // Days since last activity
    // frequency: number; // Number of transactions/bets in last 30 days
    depositedAmount: number; // Total deposits
    withdrawedAmount: number; // Total withdrawals
    balance: number; // Current balance
    monetaryValue: number; // Net value (deposits - withdrawals - balance)
  };

  /**
   * Loyalty program status and rewards points tracking.
   * Tiers progress from Bronze(1) to Diamond(5) with
   * corresponding point accumulation determining level.
   */
  loyalty?: LoyaltyTierPoints;

  /**
   * Technical access patterns tracking preferred devices
   * and network information for security and UX optimization
   */
  deviceInfo?: IDeviceInfo;
  update_at?: number;
}
export interface IDeviceInfo {
  device: DEVICE;
  ip: string;
}
export interface LoyaltyTierPoints {
  tier: LoyaltyTier;
  points: number;
}
